import React, { useState, useEffect } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { motion } from 'framer-motion';
import {
  Typography,
  Paper,
  Button,
  Divider,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  ContentCopy,
  Refresh,
  Visibility,
  VisibilityOff,
  DeleteOutline
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
  generateKey,
  clearShareKey
} from '../../../../redux/middleware/user/patient/generate_key';
import { showToast } from '../../../../utils/user/patient/helpers';
import { toast } from 'react-toastify';
import { selectIsKeyValid } from '../../../../redux/selectors/user/patient/generate_key';

const ShareMedicalDataPage = () => {
  const dispatch = useDispatch();
  const {
    key: sharingKey,
    permission,
    expires_at: keyExpiry,
    loading,
    error
  } = useSelector((state) => state.patientGenerateKey);
  const isValid = useSelector(selectIsKeyValid);

  const [allowEdit, setAllowEdit] = useState(permission === 'edit' || false);
  const [showKey, setShowKey] = useState(false);

  // Initial key generation if none exists
  useEffect(() => {
    if (!sharingKey && !loading) {
      handleGenerateKey('view');
    }
  }, [sharingKey, loading]);

  // Update allowEdit when permission changes
  useEffect(() => {
    setAllowEdit(permission === 'edit');
  }, [permission]);

  const handleGenerateKey = async (permissionType = 'view') => {
    try {
      const response = await dispatch(generateKey(permissionType));

      if (response && response.status === 201) {
        showToast(toast, 'success', 'Sharing key generated successfully');
      }
    } catch (error) {
      console.error('Failed to generate sharing key:', error);
      showToast(
        toast,
        'error',
        error.response?.data?.error ||
					error.message ||
					'Failed to generate sharing key'
      );
    }
  };

  const handleCopyKey = () => {
    if (sharingKey) {
      navigator.clipboard
        .writeText(sharingKey)
        .then(() => {
          showToast(toast, 'info', 'Sharing key copied to clipboard');
        })
        .catch((error) => {
          showToast(toast, 'error', 'Failed to copy key to clipboard');
          console.error('Failed to copy key:', error);
        });
    }
  };

  const handleRegenerateKey = () => {
    handleGenerateKey(allowEdit ? 'edit' : 'view');
  };

  const handleRevokeAccess = async () => {
    try {
      await dispatch(clearShareKey());
      showToast(toast, 'success', 'Access revoked successfully');
      // Generate a new key after revoking
      // setTimeout(() => handleGenerateKey('view'), 300);
    } catch (error) {
      showToast(toast, 'error', 'Failed to revoke access');
    }
  };

  const handlePermissionChange = (e) => {
    const newAllowEdit = e.target.checked;
    setAllowEdit(newAllowEdit);
    if (sharingKey) {
      // Generate new key with updated permission
      handleGenerateKey(newAllowEdit ? 'edit' : 'view');
    }
  };

  const formatExpiryTime = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  };

  const getTimeRemaining = () => {
    if (!keyExpiry) return '';

    const expiryDate = new Date(keyExpiry);
    const now = new Date();
    const diffMs = expiryDate - now;

    if (diffMs <= 0) return 'Expired';

    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${diffHrs}h ${diffMins}m remaining`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={css(styles.container)}
    >
      <Typography variant='h4' className={css(styles.title)}>
        Share Medical Data
      </Typography>

      <Paper elevation={3} className={css(styles.contentPaper)}>
        <Typography variant='body1' className={css(styles.description)}>
          Generate a unique key to securely share your medical records with
          healthcare providers or family members. Keys automatically expire
          after 6 hours for your security.
        </Typography>

        <Alert severity='info' className={css(styles.alert)}>
          Never share your access key with unauthorized individuals. Only share
          with trusted healthcare providers or family members.
        </Alert>

        <Divider className={css(styles.divider)} />

        <div className={css(styles.keySection)}>
          <Typography variant='h6' className={css(styles.sectionTitle)}>
            Your Sharing Key
          </Typography>

          <div className={css(styles.keyContainer)}>
            {loading
              ? (
                <div className={css(styles.loadingContainer)}>
                  <CircularProgress size={24} />
                  <Typography variant='body2'>
                    Generating secure key...
                  </Typography>
                </div>
                )
              : (
                <>
                  <div className={css(styles.keyInputContainer)}>
                    <TextField
                      className={css(styles.keyTextField)}
                      fullWidth
                      variant='outlined'
                      value={sharingKey || ''}
                      type={showKey ? 'text' : 'password'}
                      InputProps={{
										  readOnly: true,
										  endAdornment: (
  <IconButton
    onClick={() => setShowKey(!showKey)}
    edge='end'
  >
    {showKey ? <VisibilityOff /> : <Visibility />}
  </IconButton>
										  )
                      }}
                    />
                    <Button
                      variant='contained'
                      color='primary'
                      startIcon={<ContentCopy />}
                      onClick={handleCopyKey}
                      className={css(styles.copyButton)}
                      disabled={!sharingKey}
                    >
                      Copy
                    </Button>
                  </div>

                  {keyExpiry && (
                    <div className={css(styles.expiryInfo)}>
                      <Typography variant='body2'>
                        Expires: {formatExpiryTime(keyExpiry)} (
                        {getTimeRemaining()})
                      </Typography>
                    </div>
                  )}
                </>
                )}
          </div>

          <div className={css(styles.permissionToggle)}>
            <FormControlLabel
              control={
                <Switch
                  checked={allowEdit}
                  onChange={handlePermissionChange}
                  disabled={loading}
                />
							}
              label='Allow editing of records'
            />
            <Typography variant='body2' className={css(styles.permissionHint)}>
              When enabled, the person you share with can edit your treatment
              records.
            </Typography>
          </div>
        </div>

        <Divider className={css(styles.divider)} />

        <div className={css(styles.actionButtons)}>
          <Button
            variant='outlined'
            color='primary'
            startIcon={<Refresh />}
            onClick={handleRegenerateKey}
            disabled={loading}
          >
            Regenerate Key
          </Button>
          <Button
            variant='outlined'
            color='error'
            startIcon={<DeleteOutline />}
            onClick={handleRevokeAccess}
            disabled={loading || !sharingKey}
          >
            Revoke Access
          </Button>
        </div>

        <Divider className={css(styles.divider)} />

        <div className={css(styles.instructionsSection)}>
          <Typography variant='h6' className={css(styles.sectionTitle)}>
            How to Share Your Medical Data
          </Typography>
          <ol className={css(styles.instructionsList)}>
            <li>Copy the sharing key generated above.</li>
            <li>
              Send the key to the healthcare provider or family member who needs
              access to your medical records.
            </li>
            <li>
              They will need to enter this key on the "Access Shared Records"
              page.
            </li>
            <li>
              The key will automatically expire after 6 hours for security.
            </li>
            <li>
              You can revoke access at any time by clicking the "Revoke Access"
              button.
            </li>
          </ol>
        </div>
      </Paper>
    </motion.div>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: '1.5rem',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  title: {
    color: '#2783F7',
    marginBottom: '1.5rem',
    fontWeight: 'bold',
    fontSize: '1.8rem',
    '@media (max-width: 600px)': {
      fontSize: '1.5rem'
    }
  },
  contentPaper: {
    padding: '1.5rem',
    borderRadius: '8px',
    backgroundColor: '#ffffff'
  },
  description: {
    marginBottom: '1.5rem',
    color: '#555'
  },
  alert: {
    marginBottom: '1.5rem'
  },
  divider: {
    margin: '1.5rem 0'
  },
  keySection: {
    marginBottom: '1.5rem'
  },
  sectionTitle: {
    color: '#2783F7',
    marginBottom: '1rem',
    fontWeight: 'bold'
  },
  keyContainer: {
    marginBottom: '1rem'
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '1rem',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px'
  },
  keyInputContainer: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    '@media (max-width: 600px)': {
      flexDirection: 'column'
    }
  },
  keyTextField: {
    '& .MuiInputBase-root': {
      color: '#000000'
    }
  },
  copyButton: {
    backgroundColor: '#2783F7',
    '&:hover': {
      backgroundColor: '#1b6ed9'
    },
    '@media (max-width: 600px)': {
      width: '100%'
    }
  },
  expiryInfo: {
    marginTop: '0.5rem',
    color: '#f57c00'
  },
  permissionToggle: {
    marginTop: '1rem'
  },
  permissionHint: {
    color: '#666',
    marginLeft: '2.5rem',
    fontSize: '0.8rem'
  },
  actionButtons: {
    display: 'flex',
    gap: '1rem',
    '@media (max-width: 600px)': {
      flexDirection: 'column'
    }
  },
  instructionsSection: {
    marginTop: '1rem'
  },
  instructionsList: {
    marginLeft: '1.5rem',
    lineHeight: '1.6',
    color: '#000000'
  }
});

export default ShareMedicalDataPage;
